import React, { useCallback } from "react";
import { ImageComponent, ReactPortalComp } from "../../../../../components";

import { useBreakpointV2 } from "@tm/ui-widgets";
import { theme } from "@tm/utility/global";
import CommonModalDweb from "../../../../../components/CommonModalDweb/CommonModalDweb";
import { ChevronUpIcon } from "../../../../../icons";
import cancelGreyBg from "../../../../../icons/cancelGreyBg.svg";
import type { ClaimReasonData, ClaimReasonNode } from "../../../types";
import {
  Backdrop,
  ChevronWrapper,
  CloseButton,
  ModalBody,
  ReasonContent,
  ReasonRow,
  ReasonSubtitle,
  ReasonTitle,
  SheetHeader,
  SheetPanel,
  SheetTitle,
} from "./ReasonPickerModal.style";

interface ReasonPickerModalProps {
  reasonData: ClaimReasonData;
  onSelectNode: (node: ClaimReasonNode) => void;
  onClose: () => void;
}

export function ReasonPickerModal({
  reasonData,
  onSelectNode,
  onClose,
}: ReasonPickerModalProps) {
  const device = useBreakpointV2();
  const nodes = reasonData?.nodes ?? [];
  const title = reasonData?.title?.text?.value ?? "Select a reason";

  const handleNodeClick = useCallback(
    (node: ClaimReasonNode) => {
      onSelectNode(node);
      onClose();
    },
    [onSelectNode, onClose]
  );

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  if (nodes.length === 0) return null;

  const reasonList = (
    <ModalBody>
      {nodes.map((node) => {
        const nodeTitle = node?.title?.text?.value;
        const nodeSubtitle = node?.subtitle?.text?.value;

        if (!nodeTitle) return null;

        return (
          <ReasonRow
            key={node.id ?? nodeTitle}
            onClick={() => handleNodeClick(node)}
            type="button"
          >
            <ReasonContent>
              <ReasonTitle>{nodeTitle}</ReasonTitle>
              {nodeSubtitle ? (
                <ReasonSubtitle>{nodeSubtitle}</ReasonSubtitle>
              ) : null}
            </ReasonContent>
            <ChevronWrapper aria-hidden="true">
              <ChevronUpIcon color={theme.blueText} cursor="default" />
            </ChevronWrapper>
          </ReasonRow>
        );
      })}
    </ModalBody>
  );

  if (device === "sm") {
    return (
      <ReactPortalComp>
        <Backdrop onClick={handleBackdropClick}>
          <SheetPanel role="dialog" aria-modal="true" aria-label={title}>
            <SheetHeader>
              <SheetTitle>{title}</SheetTitle>
              <CloseButton onClick={onClose} aria-label="Close">
                <ImageComponent
                  src={cancelGreyBg.src}
                  width="24px"
                  height="24px"
                  alt="Close"
                />
              </CloseButton>
            </SheetHeader>
            {reasonList}
          </SheetPanel>
        </Backdrop>
      </ReactPortalComp>
    );
  }

  return (
    <CommonModalDweb
      customTitle={title}
      handleClick={onClose}
      customWidth="460px"
      customHeight="auto"
    >
      {reasonList}
    </CommonModalDweb>
  );
}
